/**
 * KI-Dachanalyse-Panel.
 *
 * Verwendung im Projekt-Detail (PV-Modus):
 *   <RoofAnalysisPanel projectId={p.id} onAccept={(kwp) => updateProjectKwp(kwp)} />
 *
 * Workflow:
 *   1. Mount → GET /api/projects/{id}/roof-analysis → wenn vorhanden, zeige Cache + „Frisch analysieren"-Button
 *   2. „KI-Analyse starten" → POST … → 5-10s Lade-Indikator → Ergebnis
 *   3. Klick auf einzelnes Feld → editierbar → PATCH speichert Override
 *   4. „In PV-Berechnung übernehmen" → propsondowardts kWp an Parent
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Satellite, Loader2, RefreshCw, Compass, Mountain, TreePine, Sun, AlertTriangle,
  Pencil, Check, X, ArrowRight, CheckCircle2, FileDown,
} from "lucide-react";
import api from "@/lib/api";

const ORIENTATION_LABELS = {
  N: "Nord", NE: "Nord-Ost", E: "Ost", SE: "Süd-Ost",
  S: "Süd", SW: "Süd-West", W: "West", NW: "Nord-West", mixed: "Mehrere Flächen",
};
const PITCH_LABELS = { flat: "Flachdach", low: "Niedrig", medium: "Mittel", steep: "Steil" };
const SHADING_LABELS = { low: "Niedrig", medium: "Mittel", high: "Hoch" };
const CONFIDENCE_PILL = {
  low: "bg-rose-500/15 text-rose-300 border-rose-500/40",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  high: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40",
};

export default function RoofAnalysisPanel({ projectId, onAccept, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/roof-analysis`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Dachanalyse nicht laden");
    } finally {
      setLoading(false);
    }
  };

  const start = async (refresh = false) => {
    setRunning(true);
    try {
      const url = refresh
        ? `/projects/${projectId}/roof-analysis/refresh`
        : `/projects/${projectId}/roof-analysis`;
      const { data } = await api.post(url);
      setData({ exists: true, analysis: data.analysis });
      onChanged?.();
      const cached = data.analysis?.from_cache;
      toast.success(cached ? "Analyse aus Cache geladen" : "KI-Analyse abgeschlossen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Analyse fehlgeschlagen");
    } finally {
      setRunning(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (projectId) load(); }, [projectId]);

  if (loading) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 text-zinc-400 inline-flex items-center gap-2" data-testid="roof-loading">
        <Loader2 size={16} className="animate-spin" /> Dachanalyse lädt…
      </div>
    );
  }

  if (!data?.exists) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="roof-empty">
        <div className="flex items-start gap-3">
          <Satellite className="text-sky-400 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="font-display text-base font-bold">KI-Dachanalyse</h3>
            <p className="text-[12px] text-zinc-400 mt-1 font-medium">
              Lass die KI das Dach per Satellitenbild auswerten und die Anlagengröße schätzen.
            </p>
            <button
              onClick={() => start(false)}
              disabled={running}
              data-testid="roof-start-btn"
              className="mt-3 h-10 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50"
            >
              {running ? <Loader2 size={14} className="animate-spin" /> : <Satellite size={14} />}
              KI-Analyse starten
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RoofAnalysisResult
      analysis={data.analysis}
      onRefresh={() => start(true)}
      onAccept={onAccept}
      onChanged={onChanged}
      running={running}
    />
  );
}

function RoofAnalysisResult({ analysis: initialAnalysis, onRefresh, onAccept, onChanged, running }) {
  const [analysis, setAnalysis] = useState(initialAnalysis);
  useEffect(() => { setAnalysis(initialAnalysis); }, [initialAnalysis]);
  const eff = analysis.manual_override || analysis.ai_result || {};
  const conf = eff.confidence || "medium";

  const handleOverrideUpdate = (manualOverride) => {
    setAnalysis((prev) => ({ ...prev, manual_override: manualOverride }));
    onChanged?.();
  };

  const [downloadingBriefing, setDownloadingBriefing] = useState(false);
  const downloadBriefing = async () => {
    setDownloadingBriefing(true);
    try {
      const res = await api.get(
        `/projects/${analysis.project_id}/roof-analysis/briefing.pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pv-briefing_${analysis.project_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PV-Vorab-Briefing heruntergeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Briefing-PDF fehlgeschlagen");
    } finally {
      setDownloadingBriefing(false);
    }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4" data-testid="roof-result">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Satellite className="text-sky-400" size={18} />
          <div>
            <h3 className="font-display text-base font-bold">KI-Dachanalyse</h3>
            <div className="text-[12px] text-zinc-300 flex items-center gap-2">
              <span>{analysis.formatted_address || "—"}</span>
              {analysis.from_cache && (
                <span className="text-amber-300/80" data-testid="roof-cache-badge">aus Cache · gültig bis {analysis.expires_at?.slice(0, 10)}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${CONFIDENCE_PILL[conf]}`} data-testid="roof-confidence">
            Konfidenz: {conf === "low" ? "Niedrig" : conf === "medium" ? "Mittel" : "Hoch"}
          </span>
          <button
            onClick={onRefresh}
            disabled={running}
            data-testid="roof-refresh-btn"
            title="Frische Analyse erzwingen (umgeht Cache)"
            className="h-8 w-8 rounded-lg bg-zinc-800 border border-zinc-700 inline-flex items-center justify-center hover:bg-zinc-700 disabled:opacity-50"
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <EditableMetric
          analysis={analysis} field="usable_roof_area_m2" type="int"
          icon={Mountain} label="Nutzbare Fläche" suffix=" m²" testid="roof-area"
          onUpdate={handleOverrideUpdate}
        />
        <EditableMetric
          analysis={analysis} field="estimated_panels" type="int"
          icon={Sun} label="Modulanzahl" testid="roof-panels"
          onUpdate={handleOverrideUpdate}
        />
        <EditableMetric
          analysis={analysis} field="estimated_kwp" type="float"
          icon={Sun} label="kWp" suffix=" kWp" testid="roof-kwp" accent="green"
          onUpdate={handleOverrideUpdate}
        />
        <EditableMetric
          analysis={analysis} field="shading_risk" type="enum"
          options={SHADING_LABELS} icon={TreePine} label="Verschattung" testid="roof-shading"
          onUpdate={handleOverrideUpdate}
        />
        <EditableMetric
          analysis={analysis} field="orientation" type="enum"
          options={ORIENTATION_LABELS} icon={Compass} label="Ausrichtung" testid="roof-orientation"
          onUpdate={handleOverrideUpdate}
        />
        <EditableMetric
          analysis={analysis} field="pitch" type="enum"
          options={PITCH_LABELS} icon={Mountain} label="Neigung" testid="roof-pitch"
          onUpdate={handleOverrideUpdate}
        />
      </div>

      {eff.obstacles?.length > 0 && (
        <div className="text-[12px] text-zinc-300 font-medium" data-testid="roof-obstacles">
          <span className="inline-flex items-center gap-1.5 text-amber-300/90 font-bold mr-2">
            <AlertTriangle size={12} /> Hindernisse:
          </span>
          {eff.obstacles.join(" · ")}
        </div>
      )}

      {eff.notes && (
        <div className="text-[12px] text-zinc-400 italic border-l-2 border-zinc-700 pl-3 font-medium" data-testid="roof-notes">
          {eff.notes}
        </div>
      )}

      {analysis.manual_override && (
        <div className="text-[12px] text-amber-300/80 inline-flex items-center gap-1" data-testid="roof-override-badge">
          <Pencil size={10} /> Werte manuell überschrieben
        </div>
      )}

      {onAccept && (
        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-zinc-800">
          <button
            onClick={downloadBriefing}
            disabled={downloadingBriefing}
            data-testid="roof-briefing-btn"
            title="Vorab-Briefing-PDF für Kundentermin"
            className="h-10 px-3 rounded-lg bg-[#18181B] border border-zinc-700 text-zinc-200 font-bold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-zinc-800 disabled:opacity-50"
          >
            {downloadingBriefing ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            Briefing-PDF
          </button>
          <button
            onClick={() => onAccept(eff.estimated_kwp, eff)}
            data-testid="roof-accept-btn"
            className="h-10 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-[#00C964]"
          >
            <CheckCircle2 size={14} />
            {eff.estimated_kwp} kWp übernehmen
            <ArrowRight size={14} />
          </button>
        </div>
      )}
      {!onAccept && (
        <div className="flex items-center justify-end pt-2 border-t border-zinc-800">
          <button
            onClick={downloadBriefing}
            disabled={downloadingBriefing}
            data-testid="roof-briefing-btn"
            className="h-10 px-3 rounded-lg bg-[#18181B] border border-zinc-700 text-zinc-200 font-bold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-zinc-800 disabled:opacity-50"
          >
            {downloadingBriefing ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            PV-Vorab-Briefing als PDF
          </button>
        </div>
      )}
    </div>
  );
}

function EditableMetric({ analysis, field, type, options, icon: Icon, label, suffix = "", testid, accent, onUpdate }) {
  const eff = analysis.manual_override || analysis.ai_result || {};
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(eff[field]);
  const [saving, setSaving] = useState(false);

  const accentColor =
    accent === "green" ? "text-[#00E575]" :
    "text-zinc-100";

  const save = async () => {
    setSaving(true);
    try {
      const payload = { [field]: type === "int" ? parseInt(val, 10) : type === "float" ? parseFloat(val) : val };
      const { data } = await api.patch(
        `/projects/${analysis.project_id}/roof-analysis/${analysis.id}`,
        payload,
      );
      onUpdate?.(data.manual_override);
      setEditing(false);
      toast.success("Wert gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const displayVal = () => {
    const v = eff[field];
    if (type === "enum") return options?.[v] || v || "—";
    return `${v ?? "—"}${suffix}`;
  };

  if (editing) {
    return (
      <div className="bg-[#0F0F11] border border-[#00E575]/40 rounded-xl p-3" data-testid={`${testid}-editing`}>
        <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1.5 inline-flex items-center gap-1">
          <Icon size={11} /> {label}
        </div>
        {type === "enum" ? (
          <select value={val || ""} onChange={(e) => setVal(e.target.value)} autoFocus
            className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-[13px]">
            {Object.entries(options).map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        ) : (
          <input
            type="number" step={type === "float" ? "0.1" : "1"} autoFocus
            value={val || ""} onChange={(e) => setVal(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-[13px]"
          />
        )}
        <div className="flex items-center gap-1 mt-2">
          <button onClick={save} disabled={saving} data-testid={`${testid}-save`}
            className="flex-1 h-7 rounded bg-[#00E575] text-[#09090B] text-[12px] font-bold inline-flex items-center justify-center gap-1 hover:bg-[#00C964] disabled:opacity-50">
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} OK
          </button>
          <button onClick={() => { setEditing(false); setVal(eff[field]); }}
            className="h-7 px-2 rounded bg-zinc-800 text-zinc-300 text-[12px] inline-flex items-center gap-1 hover:bg-zinc-700">
            <X size={11} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => { setVal(eff[field]); setEditing(true); }}
      className="group bg-[#0F0F11] border border-zinc-800 hover:border-zinc-600 rounded-xl p-3 text-left transition"
      data-testid={testid}
    >
      <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1.5 inline-flex items-center gap-1">
        <Icon size={11} /> {label}
      </div>
      <div className={`font-display text-lg font-bold ${accentColor} flex items-center justify-between gap-2`}>
        <span>{displayVal()}</span>
        <Pencil size={11} className="opacity-0 group-hover:opacity-60 text-zinc-400" />
      </div>
    </button>
  );
}
